'use client';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight,
  Check,
  Euro,
  Info,
  Lock,
  Rocket,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import NextLink from 'next/link';
import { useTranslations } from 'next-intl';
import React, { MouseEvent, useRef } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLandingCTATracking } from '@/features/landing/hooks/useLandingCTATracking';
import { siteConfig } from '@/lib/config/site';
import { cn } from '@/lib/utils';

import { useSectionVisibility } from '../../hooks/useSectionVisibility';

// --- Data ---

const pilotBenefits = [
  {
    key: 'item1',
    icon: Euro,
    hasPriceDetail: true,
    color: 'emerald',
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
  },
  {
    key: 'item2',
    icon: Rocket,
    hasPriceDetail: true,
    color: 'blue',
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
  },
  {
    key: 'item3',
    icon: Target,
    hasPriceDetail: false,
    color: 'purple',
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
  },
  {
    key: 'item4',
    icon: TrendingUp,
    hasPriceDetail: false,
    color: 'amber',
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
  },
];

const investmentItems = [
  { key: 'item1', highlight: false },
  { key: 'item2', highlight: true },
  { key: 'item3', highlight: false },
  { key: 'item4', highlight: false },
];

// --- Main Component ---

export function PilotSection() {
  const t = useTranslations('landing.pilot');
  const { trackCTAClick } = useLandingCTATracking();
  const containerRef = useRef<HTMLElement>(null);
  useSectionVisibility('pilot', { ref: containerRef });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);

  const handleCTAClick = () => {
    trackCTAClick({
      ctaLocation: 'pilot_section',
      ctaText: t('cta.button'),
      ctaType: 'primary',
      section: 'pilot',
      destinationUrl: siteConfig.bookingUrl,
    });
  };

  return (
    <section
      id="pilot"
      ref={containerRef}
      className="relative py-24 lg:py-32 bg-background overflow-hidden text-foreground border-t border-border"
    >
      {/* Dynamic Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[128px]" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-[128px]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 z-10">
        {/* Header */}
        <motion.div style={{ opacity }} className="mx-auto max-w-3xl text-center mb-16 space-y-6">
          <Badge
            variant="outline"
            className="mb-6 px-4 py-1.5 text-sm border-primary/30 bg-primary/10 text-primary"
          >
            <span className="relative flex h-2 w-2 mr-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            {t('badge')}
          </Badge>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
            {t.rich('title', {
              highlight: (chunks) => (
                <span className="text-primary relative inline-block">
                  {chunks}
                  <svg
                    className="absolute w-full h-3 -bottom-1 left-0 text-primary/20 -z-10"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 5 Q 50 10 100 5"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                    />
                  </svg>
                </span>
              ),
            })}
          </h2>

          <div className="flex flex-col items-center gap-4">
            <Badge
              variant="secondary"
              className="text-base px-4 py-2 bg-muted border border-border text-foreground"
            >
              <Users className="w-4 h-4 mr-2 text-muted-foreground" />
              {t('spotsLeft')}
            </Badge>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              {t('subtitle')}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20">
          {/* Left Column: Transparency Box */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <TiltCard>
              <Card className="h-full bg-emerald-50/50 dark:bg-emerald-950/10 backdrop-blur-xl border-emerald-200/50 dark:border-emerald-900/50 overflow-hidden shadow-xl shadow-emerald-500/5">
                <CardContent className="p-8 md:p-10 space-y-8">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <motion.div
                        className="mt-1 p-3 bg-primary/10 rounded-xl shrink-0 border border-primary/20"
                        whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Info className="w-5 h-5 text-primary" />
                      </motion.div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground mb-3">
                          {t('transparency.title')}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {t.rich('transparency.description', {
                            highlight: (chunks) => (
                              <strong className="text-foreground font-semibold">{chunks}</strong>
                            ),
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="pl-5 border-l-2 border-primary/30 space-y-3 bg-background/60 backdrop-blur-sm p-5 rounded-r-xl">
                      <p className="text-foreground/90 leading-relaxed italic">
                        {t('transparency.quote')}
                      </p>
                      <p className="text-xs text-muted-foreground font-medium">
                        {t('transparency.author')}
                      </p>
                    </div>

                    <p className="text-muted-foreground leading-relaxed">
                      {t.rich('transparency.footer', {
                        highlight: (chunks) => (
                          <span className="text-foreground font-semibold">{chunks}</span>
                        ),
                      })}
                    </p>
                  </div>

                  {/* Slots Visualization */}
                  <div className="bg-background/60 backdrop-blur-sm rounded-xl p-6 border border-border/50 shadow-sm">
                    <div className="flex items-center justify-between mb-5">
                      <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
                        {t('status.title')}
                      </h4>
                      <span className="text-xs text-primary font-semibold bg-primary/10 px-2 py-1 rounded">
                        {t('status.updated')}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {/* Slot 1: Taken */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/40 border border-border/50">
                        <div className="flex items-center gap-3 opacity-60">
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted-foreground/20">
                            <Check className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <span className="font-medium text-sm text-muted-foreground line-through">
                            {t('status.slot1')}
                          </span>
                        </div>
                        <span className="text-[10px] uppercase font-bold text-muted-foreground/50 border border-border px-2 py-1 rounded">
                          {t('status.taken')}
                        </span>
                      </div>

                      {/* Slot 2-5: Available */}
                      <motion.div
                        className="relative group overflow-hidden rounded-xl border-2 border-primary/40 bg-primary/5 p-4 transition-all hover:bg-primary/10 hover:border-primary/60"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="flex items-center justify-between relative z-10">
                          <div className="flex items-center gap-3">
                            <div className="relative flex items-center justify-center w-6 h-6">
                              <div className="absolute w-full h-full rounded-full bg-primary opacity-30 animate-ping" />
                              <div className="w-3 h-3 rounded-full bg-primary" />
                            </div>
                            <span className="font-semibold text-sm text-foreground">
                              {t('status.slotsRemaining')}
                            </span>
                          </div>
                          <Badge className="bg-primary hover:bg-primary/90 text-primary-foreground border-none text-xs font-bold">
                            {t('status.available')}
                          </Badge>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TiltCard>
          </motion.div>

          {/* Right Column: Pricing/Investment */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 flex flex-col h-full"
          >
            <TiltCard>
              <div className="h-full bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl shadow-xl hover:shadow-2xl hover:border-primary/30 transition-all duration-500 overflow-hidden">
                <div className="p-8 h-full flex flex-col relative">
                  {/* Decorative Glow */}
                  <div className="absolute -top-12 -right-12 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />

                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                      <Lock className="w-5 h-5 text-primary" />
                      {t('investment.title')}
                    </h3>

                    <div className="space-y-4 flex-grow mb-8">
                      {investmentItems.map((item, index) => (
                        <motion.div
                          key={index}
                          className="flex items-start gap-3 group"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <div
                            className={cn(
                              'mt-0.5 w-5 h-5 rounded-full flex items-center justify-center border shrink-0 transition-all duration-300',
                              item.highlight
                                ? 'border-primary bg-primary text-primary-foreground shadow-sm shadow-primary/20'
                                : 'border-border bg-muted text-muted-foreground group-hover:border-primary/30',
                            )}
                          >
                            <Check className="w-3 h-3" />
                          </div>
                          <span
                            className={cn(
                              'text-sm leading-relaxed',
                              item.highlight
                                ? 'text-foreground font-semibold'
                                : 'text-muted-foreground',
                            )}
                          >
                            {t(`investment.items.${item.key}`)}
                          </span>
                        </motion.div>
                      ))}
                    </div>

                    <div className="pt-6 mt-auto border-t border-border text-center space-y-4">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                          {t('investment.regularPrice.label')}
                        </p>
                        <p className="text-base font-medium line-through text-muted-foreground/60 decoration-red-500/50 decoration-2">
                          {t('investment.regularPrice.value')}
                        </p>
                      </div>
                      <motion.div
                        className="bg-primary/10 rounded-xl py-4 px-6 border-2 border-primary/30"
                        whileHover={{ scale: 1.02, borderColor: 'rgba(var(--primary), 0.5)' }}
                      >
                        <p className="text-lg font-bold text-primary mb-1">
                          {t('investment.pilotPrice.label')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t('investment.pilotPrice.value')}
                        </p>
                      </motion.div>
                      <p className="text-xs text-muted-foreground italic pt-2">
                        {t('investment.footer')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TiltCard>
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/20 shadow-2xl">
            {/* Background Decoration */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="relative z-10 p-8 md:p-12 text-center space-y-6">
              <motion.div
                initial={{ scale: 0.9 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Badge
                  variant="outline"
                  className="mb-6 px-4 py-1.5 text-sm border-primary/30 bg-primary/10 text-primary"
                >
                  {t('cta.badge')}
                </Badge>
              </motion.div>

              <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
                {t.rich('cta.title', {
                  highlight: (chunks) => (
                    <span className="text-primary relative inline-block">
                      {chunks}
                      <motion.svg
                        className="absolute w-full h-3 -bottom-1 left-0 text-primary/30"
                        viewBox="0 0 100 10"
                        preserveAspectRatio="none"
                        initial={{ pathLength: 0 }}
                        whileInView={{ pathLength: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                      >
                        <path
                          d="M0 5 Q 50 10 100 5"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="none"
                        />
                      </motion.svg>
                    </span>
                  ),
                })}
              </h3>

              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t('cta.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <Button
                    asChild
                    size="lg"
                    className="h-14 px-8 text-lg rounded-full shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 transition-all"
                  >
                    <NextLink
                      href={siteConfig.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleCTAClick}
                    >
                      {t('cta.button')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </NextLink>
                  </Button>
                </motion.div>
              </div>

              <p className="text-sm text-muted-foreground/80 italic pt-2">{t('cta.guarantee')}</p>
            </div>
          </div>
        </motion.div>
        {/* Benefits Grid */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 space-y-4"
          >
            <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              {t('benefits.title')}
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t('benefits.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {pilotBenefits.map((benefit, index) => (
              <BenefitCard3D key={benefit.key} benefit={benefit} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// --- 3D Benefit Card Component ---

function BenefitCard3D({ benefit, index }: { benefit: (typeof pilotBenefits)[0]; index: number }) {
  const t = useTranslations('landing.pilot');
  const Icon = benefit.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <TiltCard>
        <div
          className={cn(
            'h-full bg-card/80 backdrop-blur-xl border rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group',
            benefit.borderColor,
            'hover:border-opacity-50',
          )}
        >
          {/* Decorative Glow */}
          <div
            className={cn(
              'absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500',
              benefit.iconBg,
            )}
          />

          <div className="relative z-10 flex flex-col h-full">
            <div className="flex items-start gap-4 mb-4">
              <motion.div
                className={cn(
                  'p-3 rounded-xl border shrink-0',
                  benefit.iconBg,
                  benefit.borderColor,
                )}
                whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                transition={{ duration: 0.5 }}
              >
                <Icon className={cn('h-6 w-6', benefit.iconColor)} aria-hidden="true" />
              </motion.div>
              <div className="flex-grow">
                <h4 className="font-bold text-lg text-foreground mb-2">
                  {t(`benefits.items.${benefit.key}.title`)}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(`benefits.items.${benefit.key}.description`)}
                </p>
              </div>
            </div>

            {benefit.hasPriceDetail && benefit.key === 'item1' && (
              <div className="mt-auto pt-4 border-t border-border/50">
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-foreground">
                      {t(`benefits.items.${benefit.key}.priceDetail.price`)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t(`benefits.items.${benefit.key}.priceDetail.unit`)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground line-through">
                    {t(`benefits.items.${benefit.key}.priceDetail.regular`)}
                  </div>
                  <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                    <span className="inline-block w-1 h-4 bg-emerald-500 rounded-full"></span>
                    {t(`benefits.items.${benefit.key}.priceDetail.save`)}
                  </div>
                </div>
              </div>
            )}

            {benefit.hasPriceDetail && benefit.key === 'item2' && (
              <div className="mt-auto pt-4 border-t border-border/50">
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-foreground">
                      {t(`benefits.items.${benefit.key}.priceDetail.price`)}
                    </span>
                    <span className="text-xs text-muted-foreground line-through">
                      {t(`benefits.items.${benefit.key}.priceDetail.regular`)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-3 border-l-2 border-border">
                    {t(`benefits.items.${benefit.key}.priceDetail.note`)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </TiltCard>
    </motion.div>
  );
}

// --- 3D Tilt Card Component ---

function TiltCard({ children }: { children: React.ReactNode }) {
  const mouseX = useRef(0);
  const mouseY = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseXFromCenter = e.clientX - rect.left - width / 2;
    const mouseYFromCenter = e.clientY - rect.top - height / 2;

    mouseX.current = mouseXFromCenter / width;
    mouseY.current = mouseYFromCenter / height;

    const rotateX = mouseY.current * -8;
    const rotateY = mouseX.current * 8;

    if (cardRef.current) {
      cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.01, 1.01, 1.01)`;
    }
  };

  const handleMouseLeave = () => {
    if (cardRef.current) {
      cardRef.current.style.transform =
        'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    }
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative w-full h-full transition-transform duration-200 ease-out"
      style={{ transformStyle: 'preserve-3d' }}
    >
      {children}
    </div>
  );
}
